import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Building, LineChart, Eye } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/card';

const Investments = () => {
  const strengths = [
    {
      icon: <Building className="w-8 h-8" />,
      title: 'Entwicklerperspektive',
      description: 'Operative Erfahrung aus über 500 realisierten Projekten',
    },
    {
      icon: <LineChart className="w-8 h-8" />,
      title: 'Investorensicht',
      description: 'Verantwortung für Family Office mit ~100 Mio. EUR Volumen',
    },
    {
      icon: <Eye className="w-8 h-8" />,
      title: 'Technisches Verständnis',
      description: 'Bausubstanz und Wirtschaftlichkeit in einer Analyse',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="flex flex-col">
              <div className="space-y-6">
              <div className="inline-flex items-center space-x-2 text-brand-600 font-medium">
                <TrendingUp className="w-5 h-5" />
                <span>Investments</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-brand-900 leading-tight">
                Kapital arbeiten lassen — strukturiert, substanzbasiert, einen Schritt voraus.
              </h1>
              <p className="text-xl text-brand-700 leading-relaxed">
                Der Kapitalmarkt bietet täglich Chancen. Wer sie erkennen will, braucht mehr als
                Informationen — er braucht Erfahrung, Urteilsvermögen und die richtigen Partner.
              </p>
              <p className="text-lg text-brand-600 leading-relaxed">
                Seit über 40 Jahren begleiten wir Privatmandanten, Unternehmer und Familienverbände
                bei der strukturierten Anlage ihres Vermögens — diskret, substanzbasiert, auf Augenhöhe.
              </p>
              </div>
              <div className="mt-10">
              <Link to="/kontakt">
                <Button 
                  size="lg" 
                  className="bg-brand-900 hover:bg-brand-800 text-white px-8 py-3 text-base transition-all duration-300 hover:shadow-lg"
                >
                  Beratung anfragen
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              </div>
            </div>
            <div className="relative h-[400px] lg:h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1694702740570-0a31ee1525c7"
                alt="Modernes Bürogebäude"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Investment-Ansatz Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-900 mb-8">
            Unser Investment-Ansatz
          </h2>
          <p className="text-xl text-brand-800 leading-relaxed">
            Wir investieren nicht in Schlagzeilen — wir investieren in Substanz. Jede Entscheidung
            basiert auf strukturierter Analyse, einem bewährten internationalen Netzwerk und dem
            klaren Ziel: Vermögen langfristig aufbauen und erhalten.
          </p>
        </div>
      </section>

      {/* Strengths Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-brand-900 mb-4">
              Zwei Perspektiven, eine Expertise
            </h2>
            <p className="text-xl text-brand-600 max-w-3xl mx-auto">
              Die Kombination aus Entwickler- und Investorenerfahrung macht den Unterschied
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {strengths.map((strength, index) => (
              <Card key={index} className="border-2 border-brand-200 hover:border-brand-400 transition-all duration-300 hover:shadow-lg">
                <CardHeader className="p-8 text-center">
                  <div className="text-brand-700 mb-4 flex justify-center">
                    {strength.icon}
                  </div>
                  <CardTitle className="text-2xl mb-3">{strength.title}</CardTitle>
                  <CardDescription className="text-base text-brand-600">
                    {strength.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Deep Dive Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-brand-900 mb-6">
                Immobilien sind mehr als Zahlen
              </h2>
              <p className="text-lg text-brand-600 leading-relaxed mb-6">
                Bei Immobilieninvestments geht es nicht nur um Renditen auf dem Papier. 
                Es geht um die Substanz, die Entwicklungsfähigkeit und die realistischen Risiken.
              </p>
              <p className="text-lg text-brand-600 leading-relaxed mb-8">
                Wir begleiten Sie bei der Bewertung von Investitionsmöglichkeiten mit einem 
                Blick, der sowohl die technische Realität als auch die wirtschaftlichen 
                Potenziale berücksichtigt.
              </p>
              <div className="space-y-4">
                <div className="p-4 bg-brand-50 rounded-lg">
                  <h3 className="font-bold text-brand-900 mb-2">Substanzbewertung</h3>
                  <p className="text-brand-600">
                    Technische Analyse der Bauqualität und des Erhaltungszustands
                  </p>
                </div>
                <div className="p-4 bg-brand-50 rounded-lg">
                  <h3 className="font-bold text-brand-900 mb-2">Entwicklungspotenzial</h3>
                  <p className="text-brand-600">
                    Einschätzung möglicher Wertsteigerungen und Optimierungen
                  </p>
                </div>
                <div className="p-4 bg-brand-50 rounded-lg">
                  <h3 className="font-bold text-brand-900 mb-2">Wirtschaftlichkeit</h3>
                  <p className="text-brand-600">
                    Realistische Bewertung von Kosten, Risiken und Renditen
                  </p>
                </div>
              </div>
            </div>
            <div className="relative h-[600px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.pexels.com/photos/4067525/pexels-photo-4067525.jpeg"
                alt="Immobilienanalyse"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-brand-900 mb-8 text-center">
            Erfahrung, die zählt
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-white rounded-xl shadow-sm">
              <div className="text-4xl font-bold text-brand-900 mb-2">40+</div>
              <div className="text-brand-600 font-medium">Jahre operative Erfahrung</div>
            </div>
            <div className="text-center p-8 bg-white rounded-xl shadow-sm">
              <div className="text-4xl font-bold text-brand-900 mb-2">500+</div>
              <div className="text-brand-600 font-medium">Realisierte Projekte</div>
            </div>
            <div className="text-center p-8 bg-white rounded-xl shadow-sm">
              <div className="text-4xl font-bold text-brand-900 mb-2">~100M</div>
              <div className="text-brand-600 font-medium">EUR verwaltetes Volumen</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services for Investors */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-brand-900 mb-12 text-center">
            Unsere Leistungen für Investoren
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-8 bg-brand-50 rounded-xl hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-bold text-brand-900 text-xl mb-3">Due Diligence</h3>
              <p className="text-brand-600">
                Umfassende technische und wirtschaftliche Prüfung vor dem Erwerb
              </p>
            </div>
            <div className="p-8 bg-brand-50 rounded-xl hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-bold text-brand-900 text-xl mb-3">Portfoliobewertung</h3>
              <p className="text-brand-600">
                Analyse und Optimierung bestehender Immobilienbestände
              </p>
            </div>
            <div className="p-8 bg-brand-50 rounded-xl hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-bold text-brand-900 text-xl mb-3">Entwicklungsberatung</h3>
              <p className="text-brand-600">
                Begleitung von Projektentwicklungen aus technischer Sicht
              </p>
            </div>
            <div className="p-8 bg-brand-50 rounded-xl hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-bold text-brand-900 text-xl mb-3">Risikomanagement</h3>
              <p className="text-brand-600">
                Identifikation und Bewertung technischer und wirtschaftlicher Risiken
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center flex flex-col items-center gap-8">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-900">
            Investieren Sie in Substanz, nicht nur in Zahlen
          </h2>
          <p className="text-xl text-brand-600 max-w-3xl">
            Lassen Sie uns gemeinsam Ihre Investitionsmöglichkeiten analysieren.
          </p>
          <Link to="/kontakt">
            <Button 
              size="lg" 
              className="bg-brand-900 hover:bg-brand-800 text-white px-10 py-3 text-lg transition-all duration-300 hover:shadow-lg"
            >
              Erstgespräch vereinbaren
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Investments;
